import { TranslationKey } from '@Localization';
import { inject, injectable, interfaces, multiInject } from 'inversify';
import { action, makeObservable } from 'mobx';
import { TRAINING_VARIANT } from '../../../modules/jobs/jobs.model';
import { MarkingToolType } from '../../../modules/projects/details/stats/annotationTools/models/AnnotationToolsStats';
import { ICurrentWorkspaceStore } from '../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { browserHistory } from '../../../usehistory';
import { delay } from '../../helpers/function.helpers';
import { ACDLError, ForbiddenError, InputStatus } from '../../models/error.model';
import { SortingDirection } from '../../models/sortingDirection.model';
import { timeZones } from '../../models/timeZones/timeZones.model';
import { ProjectRole } from '../../models/userRole.model';
import { Home } from '../../routes/config/Home';
import { IEventBus } from '../../services/eventBus.service';
import { QuestionType } from '../annotation/question.model';
import { NotificationLevel, ToastNotification } from '../notifications/models/notification.model';
import { INotificationsService } from '../notifications/services/notifications.service';
import { ProjectStatus } from '../projects/projects.model';
import { UserRemovedFromProject } from './events/userRemovedFromProject';
import { ICanPublishProjectResponse, IDatasetsTabDto, ImageAssignmentPolicy } from './projectDetails.models';
import { IProjectDetailsStore } from './projectDetails.store';
import { IProjectDetailsSubmodule } from './services/IProjectDetailsSubmodule';
import { IProjectDetailsApiService } from './services/projectDetailsApi.service';
import { IProjectDetailsStatisticsStore } from './sub/statistics/projectDetailsStatistics.store';

const getProjectDetailsFailedMessage: TranslationKey = 'get_project_details_failed';
const updateProjectDetailsFailedMessage: TranslationKey = 'update_project_details_failed';

const ERROR_CODES: Record<string, TranslationKey> = {
  PROJECT_NAME_EMPTY: 'field_cant_be_empty',
  PROJECT_NAME_NOT_UNIQUE: 'error_project_name_must_be_unique',
  NO_DATASET_ADDED: 'no_dataset_added',
  NO_SEGMENTATION_OR_QUESTION_ADDED: 'no_segmentations_or_questions_added',
};

export interface IProjectDetailsBl {
  initialize(): void;

  // datasets
  unlinkDatasetAsync(datasetId: string): Promise<void>;
  assignDatasetToProjectAsync(datasetId: string): Promise<void | ACDLError>;
  getProjectDetailsDatasetsAsync(projectId: string): Promise<IDatasetsTabDto | ACDLError>;
  changeDatasetOrderAsync(from: number, to: number): Promise<void>;

  // users
  getProjectDetailsMembersAsync(projectId: string): Promise<void | ACDLError>;
  addUserToProjectAsync(projectId: string, email: string, role: ProjectRole): Promise<void | ACDLError>;
  removeUserFromProjectAsync(userId: string, projectId: string): Promise<void>;
  usersSortChanged(orderBy: string, orderType: SortingDirection): void;
  usersPaginationChange(pageNumber: number, pageSize: number): void;

  // overview
  getProjectDetailsAsync(projectId: string): Promise<void | ACDLError>;
  changeName(name: string): void;
  nameChangedAsync(): Promise<void>;
  changeDescription(description: string): void;
  descriptionChangedAsync(): Promise<void>;
  createProjectAsync(projectId: string, projectName: string, trainingVariant: TRAINING_VARIANT | null): Promise<void | ACDLError>;
  updateImageAssignmentPolicyAsync(policy: ImageAssignmentPolicy): Promise<void>;
  getProjectAnnotationTemplate(trainingVariant: TRAINING_VARIANT): Promise<{ question: null | QuestionType; marking_tool: null | MarkingToolType }>;

  validateNameAsync(): Promise<void>;
  validateDescription(): void;
  validateDatasets(): void;

  publishAsync(projectId: string, discardDrafts: boolean): Promise<boolean>;
  canPublishAsync(): Promise<boolean>;

  store: IProjectDetailsStore;
}

export namespace IProjectDetailsBl {
  export const $: interfaces.ServiceIdentifier<IProjectDetailsBl> = Symbol('PROJECT_DETAILS_SERVICE');
}

@injectable()
export class ProjectDetailsBl implements IProjectDetailsBl {
  constructor(
    @inject(IProjectDetailsStore.$) public readonly store: IProjectDetailsStore,
    @inject(IProjectDetailsStatisticsStore.$) private readonly statisticsStore: IProjectDetailsStatisticsStore,
    @inject(INotificationsService.$) private readonly notificationsService: INotificationsService,
    @inject(IProjectDetailsApiService.$) private readonly projectDetailsApiService: IProjectDetailsApiService,
    @inject(IEventBus.$) private readonly eventBus: IEventBus,
    @inject(ICurrentWorkspaceStore.$) private readonly currentWorkspaceStore: ICurrentWorkspaceStore,
    @multiInject(IProjectDetailsSubmodule.$) private readonly submodules: IProjectDetailsSubmodule[]
  ) {
    makeObservable(this);
  }

  @action
  initialize(): void {
    this.submodules.forEach((x) => {
      x.initialize();
    });
    this.resetStore();
  }

  @action
  handleCanPublishCheck(response: ICanPublishProjectResponse): void {
    const nameErrors: TranslationKey[] = [];
    if (!response.isNameValid) {
      nameErrors.push('field_cant_be_empty');
    }
    if (!response.isNameUnique) {
      nameErrors.push('error_project_name_must_be_unique');
    }
    this.store.nameStatus = InputStatus.buildFrom(nameErrors);

    this.store.datasetsStatus = response.areDatasetsValid ? InputStatus.valid() : InputStatus.buildFrom(['no_dataset_added']);
    this.store.usersStatus = InputStatus.valid();
    this.store.descriptionStatus = InputStatus.valid();
    this.store.imagesStatus = InputStatus.valid();
  }

  @action
  resetStore(): void {
    this.store.nameStatus = InputStatus.empty();
    this.store.descriptionStatus = InputStatus.empty();
    this.store.datasetsStatus = InputStatus.empty();
    this.store.usersStatus = InputStatus.empty();
    this.store.imagesStatus = InputStatus.empty();
  }

  @action
  async validateAsync(): Promise<void> {
    await this.validateNameAsync();
    this.validateDescription();
    this.validateDatasets();
    this.validateUsers();
    this.validateImages();
  }

  getValidationErrors(): string[] {
    return [
      ...this.store.nameStatus.errorCodes,
      ...this.store.descriptionStatus.errorCodes,
      ...this.store.datasetsStatus.errorCodes,
      ...this.store.usersStatus.errorCodes,
    ];
  }

  @action
  changeName(name: string): void {
    this.store.name = name;
    this.store.nameStatus = InputStatus.empty();
  }

  @action
  changeDescription(description: string): void {
    this.store.description = description;
    this.store.descriptionStatus = InputStatus.empty();
  }

  async nameChangedAsync(): Promise<void> {
    await this.validateNameAsync();

    if (this.store.nameStatus.isValid) {
      await this.updateProjectDetailsAsync();
    }
  }

  async descriptionChangedAsync(): Promise<void> {
    this.validateDescription();
    await this.updateProjectDetailsAsync();
  }

  @action
  validateDescription(): void {
    this.store.descriptionStatus = InputStatus.valid();
  }

  @action
  async validateNameAsync(): Promise<void> {
    const nameErrorCodes: TranslationKey[] = [];

    if (this.store.name === '' && this.store.name.length === 0) {
      nameErrorCodes.push(ERROR_CODES.PROJECT_NAME_EMPTY);
    } else {
      const result = await this.projectDetailsApiService.checkProjectNameUniquenessAsync(
        this.store.id,
        this.currentWorkspaceStore.currentWorkspace.id,
        this.store.name
      );

      if (result instanceof ACDLError) {
        await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, 'name_validation_failed'));
        throw result;
      }

      if (!result) {
        nameErrorCodes.push(ERROR_CODES.PROJECT_NAME_NOT_UNIQUE);
      }
    }

    this.store.nameStatus = InputStatus.buildFrom(nameErrorCodes);
  }

  @action
  validateDatasets(): void {
    if (this.store.status === ProjectStatus.Published) {
      return;
    }
    this.store.datasetsStatus = this.store.datasets.length ? InputStatus.valid() : InputStatus.buildFrom(['no_dataset_added']);
  }

  @action
  async assignDatasetToProjectAsync(datasetId: string): Promise<void> {
    const result = await this.projectDetailsApiService.assignDatasetAsync(this.store.id, datasetId);
    await this.notifyAboutApiError(result, 'assign_dataset_to_project_failed', true);
    await this.getProjectDetailsDatasetsAsync(this.store.id);
    this.validateDatasets();
  }

  @action
  async unlinkDatasetAsync(datasetId: string): Promise<void> {
    const result = await this.projectDetailsApiService.unlinkDatasetAsync(this.store.id, datasetId);
    await this.notifyAboutApiError(result, 'unlink_dataset_failed');
    await this.getProjectDetailsDatasetsAsync(this.store.id);
    this.validateDatasets();
  }

  @action
  async getProjectDetailsAsync(projectId: string): Promise<void> {
    const result = await this.projectDetailsApiService.getProjectDetailsAsync(projectId);
    await this.notifyAboutApiError(result, getProjectDetailsFailedMessage);
    if (result instanceof ACDLError) {
      return;
    }

    if (result.workspaceId !== this.currentWorkspaceStore.currentWorkspace.id) {
      browserHistory.replace(Home.Projects.List.All.withParams({ workspaceId: this.currentWorkspaceStore.currentWorkspace.id }));
      return;
    }

    this.store.id = projectId;
    this.store.name = result.name;
    this.store.description = result.description;
    this.store.lastAnnotationDate = result.lastAnnotationDate;
    this.store.status = result.status;
    this.store.hasImportedAnnotations = result.hasImportedAnnotations;
    this.store.modelVariant = result.modelVariant;

    this.store.imageAssignmentPolicy = result.imageAssignmentPolicy as ImageAssignmentPolicy;

    this.store.createdDate = result.createdDate ?? '';
    this.store.createdBy = result.createdBy ?? '';
    this.statisticsStore.offset = result.offset;
    this.statisticsStore.statisticsGeneratedOn = result.statisticsGeneratedOn;
    const timezone = timeZones.find((tz) => tz.value === this.statisticsStore.offset);
    if (timezone) {
      this.statisticsStore.selectedTimeZone = timezone;
    }
  }

  @action
  async getProjectDetailsDatasetsAsync(projectId: string): Promise<ACDLError | IDatasetsTabDto> {
    const workspaceId = this.currentWorkspaceStore.currentWorkspace.id;
    const result = await this.projectDetailsApiService.getProjectDetailsDatasetsAsync(projectId, workspaceId);
    await this.notifyAboutApiError(result, 'get_project_datasets_failed');
    if (result instanceof ACDLError) {
      return result;
    }

    this.store.datasets.replace(result.projectDatasets);
    this.store.availableDatasets.replace(result.availableDatasets);

    if (!this.store.datasetsStatus.isEmpty) {
      this.validateDatasets();
    }

    return result;
  }

  @action
  async updateProjectDetailsAsync(): Promise<void> {
    this.store.isReadyForPublish = false;
    const payload = {
      name: this.store.name,
      description: this.store.description,
      projectId: this.store.id,
    };
    const result = await this.projectDetailsApiService.updateProjectDetailsAsync(payload);
    await this.notifyAboutApiError(result, updateProjectDetailsFailedMessage);
    this.store.isReadyForPublish = true;
    if (result instanceof ACDLError) {
      return;
    }
  }

  @action
  async addUserToProjectAsync(projectId: string, email: string, role: ProjectRole): Promise<void> {
    const result = await this.projectDetailsApiService.addUserToProjectAsync(projectId, email, role);
    await this.notifyAboutApiError(result, 'adding_user_to_project_failed');
    if (result instanceof ACDLError) {
      return;
    }

    await this.getProjectDetailsMembersAsync(projectId);
    this.validateUsers();
  }

  @action
  async removeUserFromProjectAsync(userId: string, projectId: string): Promise<void> {
    const result = await this.projectDetailsApiService.removeUserFromProjectAsync(userId, projectId);
    await this.notifyAboutApiError(result, updateProjectDetailsFailedMessage);
    if (result instanceof ACDLError) {
      return;
    }

    await this.getProjectDetailsMembersAsync(projectId);

    this.validateUsers();

    this.eventBus.sendEvent(new UserRemovedFromProject());
  }

  validateUsers(): void {
    if (this.store.status === ProjectStatus.Published) {
      return;
    }
    this.store.usersStatus = InputStatus.valid();
  }

  validateImages(): void {
    if (this.store.status === ProjectStatus.Published) {
      return;
    }
    this.store.imagesStatus = InputStatus.valid();
  }

  @action
  async getProjectDetailsMembersAsync(projectId: string): Promise<void> {
    const result = await this.projectDetailsApiService.getProjectDetailsMembersAsync(projectId);
    await this.notifyAboutApiError(result, getProjectDetailsFailedMessage);
    if (result instanceof ACDLError) {
      return;
    }

    this.store.id = projectId;
    this.store.users.replace(
      result.users.map((u) => ({
        ...u,
        joinDate: u.joinDate,
        lastLoginDate: u.lastLoginDate,
      }))
    );
    this.store.usersPagination.totalCount = this.store.users.length;
    this.store.usersPagination.pageNumber = 1;
  }

  @action
  async publishAsync(projectId: string, discardDrafts: boolean): Promise<boolean> {
    const value = await this.projectDetailsApiService.publishAsync(projectId, discardDrafts);

    if (value instanceof ACDLError && !(value instanceof ForbiddenError)) {
      if (value.withCode(['PROJECT_ALREADY_PUBLISHED'])) {
        await this.notificationsService.push(new ToastNotification(NotificationLevel.WARNING, { template: 'project_is_already_published' }));
        browserHistory.push(
          Home.Projects.Details.Overview.withParams({ projectId: this.store.id, workspaceId: this.currentWorkspaceStore.currentWorkspace.id })
        );
      } else if (value.withCode(['PROJECT_DRAFT_NOT_FOUND'])) {
        await this.notificationsService.push(new ToastNotification(NotificationLevel.WARNING, { template: 'project_not_found' }));
        browserHistory.push(Home.Projects.List.All.withParams({ workspaceId: this.currentWorkspaceStore.currentWorkspace.id }));
      } else {
        await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, { template: 'something_went_wrong' }));
      }
      return false;
    }
    this.cleanup();

    return true;
  }

  @action
  async canPublishAsync(): Promise<boolean> {
    let circuitBreaker = 100;
    while (circuitBreaker && !this.store.isReadyForPublish) {
      circuitBreaker = circuitBreaker - 1;
      // eslint-disable-next-line no-await-in-loop -- wait for project data to upodate properly
      await delay(50);
    }

    const response = await this.projectDetailsApiService.canPublishAsync(this.store.id);
    if (response instanceof ACDLError) {
      return false;
    }

    this.handleCanPublishCheck(response);
    this.submodules.forEach((x) => {
      x.handleCanPublishCheck(response);
    });
    const errors = this.getValidationErrors().concat(...this.submodules.map((x) => x.getValidationErrors()));
    return errors.length === 0;
  }

  @action
  cleanup(): void {
    this.resetStore();
    this.submodules.forEach((x) => {
      x.cleanup();
    });
  }

  @action
  async createProjectAsync(projectId: string, projectName: string, trainingVariant: TRAINING_VARIANT | null): Promise<void | ACDLError> {
    const workspaceId = this.currentWorkspaceStore.currentWorkspace.id;
    const result = await this.projectDetailsApiService.createProjectAsync(projectId, projectName, trainingVariant, workspaceId);
    if (result instanceof ACDLError && !(result instanceof ForbiddenError)) {
      await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, 'project_creating_failed'));
      return result;
    }
    return Promise.resolve(undefined);
  }

  async notifyAboutApiError(result: unknown, errorCode: TranslationKey, dontShowBadRequest: boolean = false): Promise<void> {
    if (result instanceof ACDLError && !(result instanceof ForbiddenError) && (!dontShowBadRequest || !result.isBadRequest())) {
      await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, errorCode));
    }
  }

  async changeDatasetOrderAsync(from: number, to: number): Promise<void> {
    const datasetId = this.store.datasets[from].id;
    this.store.reorderDatasets(datasetId, to);

    const result = await this.projectDetailsApiService.updateProjectDatasetsOrderAsync({
      projectId: this.store.id,
      datasetsOrders: this.store.datasets.map((x) => ({ id: x.id, order: x.order })),
    });

    await this.notifyAboutApiError(result, 'updating_project_dataset_failed', true);
  }

  async updateImageAssignmentPolicyAsync(policy: ImageAssignmentPolicy): Promise<void> {
    const result = await this.projectDetailsApiService.updateProjectImageAssignmentPolicy({
      policy,
      projectId: this.store.id,
    });

    this.store.imageAssignmentPolicy = policy;
    await this.notifyAboutApiError(result, 'updating_project_dataset_failed', true);
  }

  @action
  usersPaginationChange(pageNumber: number, pageSize: number): void {
    this.store.usersPagination.pageNumber = pageNumber;
    this.store.usersPagination.pageSize = pageSize;
  }

  @action
  usersSortChanged(orderBy: string, orderType: SortingDirection): void {
    this.store.usersOrderBy = orderBy;
    this.store.usersOrderDirection = orderType;
  }

  @action
  async getProjectAnnotationTemplate(trainingVariant: TRAINING_VARIANT): Promise<{ question: null | QuestionType; marking_tool: null | MarkingToolType }> {
    const { id } = this.currentWorkspaceStore.currentWorkspace;

    const result = await this.projectDetailsApiService.getProjectAnnotationTemplateAsync(id, trainingVariant);

    if (result instanceof ACDLError) {
      await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, 'get_project_annotation_template_failed'));
      return { question: null, marking_tool: null };
    }

    return result;
  }
}
